import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Building2, Stethoscope, UtensilsCrossed, HeartPulse, ExternalLink } from "lucide-react";
import Icon3D from "@/components/common/Icon3D";
import SplitHeading from "@/components/common/SplitHeading";

const industries = [
  {
    icon: Building2,
    title: "Real Estate",
    use: "WhatsApp AI that qualifies buyers, shares brochures and books site visits — even at 11 PM.",
    metric: "+62% site visits booked",
    demoUrl: "#",
    demoLabel: "Preview Real Estate Demo",
  },
  {
    icon: Stethoscope,
    title: "Clinics",
    use: "Automated appointment reminders, AI receptionists and follow-up campaigns that bring patients back.",
    metric: "-48% no-shows",
    demoUrl: "#",
    demoLabel: "Preview Clinic Demo",
  },
  {
    icon: HeartPulse,
    title: "Healthcare",
    use: "Patient onboarding, intake forms, post-care follow-ups and review collection on autopilot.",
    metric: "+3× 5-star reviews",
    demoUrl: "#",
    demoLabel: "Preview Healthcare Demo",
  },
  {
    icon: UtensilsCrossed,
    title: "Restaurants",
    use: "WhatsApp ordering, table reservations, review automation and loyalty — one AI handles it all.",
    metric: "+2.4× direct orders",
    demoUrl: "#",
    demoLabel: "Preview Restaurant Demo",
  },
];

const accentClasses = ["bg-peach/40", "bg-mint/40", "bg-sky/40", "bg-sand/60"];

export default function Industries() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    if (!sectionRef.current) return;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            toggleActions: "play none none none",
          },
        }
      );

      gsap.fromTo(
        cardsRef.current.filter(Boolean),
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%",
            toggleActions: "play none none none",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="industries" ref={sectionRef} className="section relative" data-testid="industries-section">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="container-x relative">
        <div ref={headerRef} className="text-center max-w-3xl mx-auto" style={{ opacity: 0 }}>
          <span className="eyebrow"><span className="dot" /> Industries</span>
          <SplitHeading
            as="h2"
            className="h-display mt-5 text-4xl sm:text-5xl lg:text-6xl text-[var(--body-fg)]"
            type="words"
            y={50}
            stagger={0.04}
            start="top 85%"
          >
            Made for the businesses that <span className="font-serif italic">drive India forward.</span>
          </SplitHeading>
          <p className="mt-5 text-[var(--body-fg)]/60">
            We&apos;ve designed industry-specific AI playbooks. No generic copy-paste — every system
            is shaped around how your sector actually works.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
          {industries.map((it, i) => {
            const Icon = it.icon;
            return (
              <article
                key={it.title}
                ref={(el) => (cardsRef.current[i] = el)}
                className={`group relative glass rounded-3xl p-7 tilt-card overflow-hidden glow-border ${accentClasses[i]}`}
                data-testid={`industry-card-${i}`}
                style={{ opacity: 0 }}
              >
                <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl opacity-40 group-hover:opacity-70 transition duration-500 bg-white" />
                <div className="relative flex items-start justify-between">
                  <Icon3D icon={Icon} size="h-13 w-13" iconSize="w-6 h-6" />
                  <div className="text-xs font-mono text-[var(--body-fg)]/50 tracking-widest">0{i + 1}</div>
                </div>
                <h3 className="relative mt-6 text-2xl font-semibold text-[var(--body-fg)] tracking-tight">{it.title}</h3>
                <p className="relative mt-3 text-sm text-[var(--body-fg)]/65 leading-relaxed">{it.use}</p>
                <div className="relative mt-6 pt-4 border-t border-[var(--hairline)] flex items-center justify-between">
                  <span className="text-xs text-[var(--body-fg)]/45 font-mono tracking-wider uppercase">Avg. Impact</span>
                  <span className="text-sm text-[var(--brand)] font-medium">{it.metric}</span>
                </div>

                <a
                  href={it.demoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="relative mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--body-fg)] hover:text-[var(--brand)] transition-colors"
                  data-testid={`industry-demo-${i}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {it.demoLabel}
                </a>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
